import React, { useEffect, useRef, useState } from "react";
import { DropdownItem, Input, InputGroup } from "reactstrap";

interface Props {
    heading: string;
    listItems: any[];
    hideNoResultsFound?: boolean;
    selectedId: number | string | null;
    setSelectedId: (id: number | null) => void;
    onBlur?: any;
    handleInputChange?: (value: string) => void;
}

const SearchableDropdownClear = ({ heading, hideNoResultsFound, listItems, selectedId, setSelectedId, onBlur, handleInputChange}: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(selectedId);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null); // Ref for the dropdown menu

    useEffect(() => {
        const selectedItem = listItems.find(item => item.value === selectedId);
        if (selectedItem) {
            setSearchTerm(selectedItem.name);
            const index = listItems.indexOf(selectedItem);
            setActiveIndex(index);
        }
    }, [selectedId, listItems]);

    useEffect(()=>{
        const selectedItem = listItems.find(item => item.value === selectedId);
        if(selectedItem){
            setSearchTerm(selectedItem.name)
        }
    },[selectedId])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setActiveIndex(-1); // Reset active index when closing dropdown
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (menuRef.current && activeIndex >= 0) {
            const menu = menuRef.current;
            const item = menu.querySelector(`[data-index="${activeIndex}"]`);
            if (item) {
                (item as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [activeIndex, isOpen]);

    const filteredList = searchTerm
    ? listItems.filter(item => item.name.toLowerCase().includes(searchTerm.toString().toLowerCase()))
    : listItems;

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setIsOpen(true);
        setActiveIndex(-1); // Reset active index when search term changes
        if (handleInputChange && event.target.value) {
            handleInputChange(event.target.value);
        }
    };

    const handleClear = () => {
        setSearchTerm('');
        setSelectedId(null);
    };

    const handleSelect = (item: any) => {
        setSelectedId(item.value);
        setSearchTerm(item.name);
        setIsOpen(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            setActiveIndex(prevIndex => Math.min(prevIndex + 1, filteredList.length - 1));
        } else if (event.key === 'ArrowUp') {
            setActiveIndex(prevIndex => Math.max(prevIndex - 1, 0));
        } else if (event.key === 'Enter' && activeIndex >= 0) {
            handleSelect(filteredList[activeIndex]);
        }
    };

    const primaryColor = '#007bff'; // Example primary color

    return (
        <div ref={dropdownRef} style={{ width: '100%', position: 'relative' }}>
            <InputGroup style={{ position: 'relative' }}>
            <Input
                type="text"
                placeholder={heading || "Select an option..."}
                value={searchTerm || ''}
                onChange={handleSearch}
                onFocus={() => setIsOpen(true)}
                onBlur={()=>{
                    if(onBlur){
                        onBlur(searchTerm)
                    }
                }}
                onKeyDown={handleKeyDown} // Handle keydown events
                style={{ paddingRight: '80px' }} // Add space for both the clear text and the dropdown icon
            />

            {/* Clear Text */}
            {searchTerm && (
                <p
                onClick={handleClear}
                style={{
                    position: 'absolute',
                    zIndex: 5,
                    right: '32px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px',
                    color: '#D3D3D3',
                    margin: '0',
                    cursor: 'pointer',
                }}
                >
                CLEAR
                </p>
            )}

            {/* Dropdown Arrow Icon */}
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-chevron-down"
                viewBox="0 0 16 16"
                style={{
                position: 'absolute',
                zIndex: 5,
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none', // Prevent the icon from blocking input clicks
                }}
            >
                <path
                fillRule="evenodd"
                d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                />
            </svg>
            </InputGroup>
            {isOpen && (
                <div
                    ref={menuRef}
                    style={{
                        width: '100%',
                        maxHeight: '40vh',
                        overflowY: 'auto',
                        position: 'absolute',
                        zIndex: 1000,
                        borderRadius: 0,
                        borderTop: 'none',
                        backgroundColor: '#fff',
                    }}
                >
                    {filteredList.length > 0 ? (
                        filteredList.map((item: any, index: number) => (
                            <DropdownItem
                                key={item.value}
                                onClick={() => handleSelect(item)}
                                active={item.value === selectedId || index === activeIndex}
                                data-index={index}
                                style={{
                                    backgroundColor: index === activeIndex ? primaryColor : '',
                                    color: index === activeIndex ? '#fff' : '',
                                }}
                            >
                                {item.name}
                            </DropdownItem>
                        ))
                    ) : (
                        hideNoResultsFound? <></> :  <DropdownItem disabled>No results found</DropdownItem>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableDropdownClear;



// WEBPACK FOOTER //
// ./src/components/common/SearchableDropdownClear.tsx